import { mapState } from 'vuex'
import templatesService from '@/api/templatesService'
import targetsService from '@/api/targetsService'
import productsService from '@/api/productsService'
import confsService from '@/api/confsService'
import tasksService from '@/api/tasksService'
import downloadBlob from '@/utils/downloadBlob'
import authService, { USER_ROLES } from '@/api/authService'
import { MODULE_NAME as MODULE_NAME_FILTERS } from '@/store/filters/constants'
import { TARGET_OPTIONS_LABELS } from '@/utils/adminOptions'
import { EXCEL_MIME_TYPE } from '@/utils/csvUtils'

const PAGE_SIZE = 50
const PRODUCT_POLLING_INTERVAL = 5000 // ms
const MESSAGE_TIMEOUT = 5000 // ms

export default {
  name: 'Admin',
  mixins: [],
  components: {},
  props: {},
  data () {
    return {
      EXCEL_MIME_TYPE: EXCEL_MIME_TYPE,
      authData: authService.authData,
      loading: false,
      activeTab: 'templates',
      templates: undefined,
      targets: undefined,
      products: undefined,
      confs: undefined,
      tasks: undefined,
      productUploadInterval: null,
      productUploadState: null,
      priceUploadLoading: false,
      productSimpleUploadLoading: false,
      showProductMessage: false,
      productMessageTimer: null,
      productUploadError: false,
      productDownloadLoading: false,
      priceDownloadLoading: false,
      targetLabels: TARGET_OPTIONS_LABELS
    }
  },
  computed: {
    ...mapState(MODULE_NAME_FILTERS, [ 'filters' ]),
    productUploadLoading () {
      return this.productUploadState === 'PENDING'
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.AGENCY ||
        this.authData.user.role === USER_ROLES.ROOT
    },
    isSuperAdmin () {
      return this.authData.user.role === USER_ROLES.ROOT
    },
    isCartoAdmin () {
      return this.authData.user.first_name === 'admincarto'
    }
  },
  mounted () {
    this.getTemplates()
    this.getProductUploadState()
    this.setProductLoadingInterval()
  },
  beforeDestroy () {
    clearTimeout(this.productMessageTimer)
    this.clearProductLoadingInterval()
  },
  watch: {
    filters () {
      this.getProducts()
    }
  },
  filters: {
    date (dateStr) {
      if (!dateStr) {
        return null
      }
      const date = new Date(dateStr).toLocaleDateString()
      const time = new Date(dateStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      return `${time} - ${date}`
    },
    translateTaskStatus (text) {
      const statuses = {
        PENDING: 'PENDIENTE',
        STARTED: 'EN PROCESO',
        RETRY: 'REINICIADO',
        FAILURE: 'ERROR',
        SUCCESS: 'FINALIZADO'
      }

      return statuses[text]
    },
    translateTask (text) {
      const taskNames = {
        products_cleanup: 'Limpieza de productos',
        backend_cleanup: 'Limpieza de datos',
        ga_analyser: 'Recolección de resultados de Google Ads',
        ga_history_analyser: 'Recolección histórica de resultados de Google Ads',
        sonata_analyser: 'Recolección de resultados de Sonata',
        sonata_status_analyser: 'Análisis de estados de Sonata',
        sonata_history_analyser: 'Recolección histórica de resultados de Sonata',
        products_uploader: 'Carga de productos masiva',
        products_updater: 'Carga de productos desde feed',
        generates_products_price_file: 'Generación de plantilla de precios',
        task_state_analyser: 'Análisis de estado de tareas',
        results_uploader: 'Carga de resultados',
        results_analyzer_status: 'Análisis de resultados',
        shop_influence_area_generator: 'Generación de áreas de influencia',
        sonata_campaing_activation: 'Activación de campañas en Sonata',
        request_efficiency: 'Cálculo de eficiencia',
        fb_label_analyser: 'Recolección de labels de FB e IG',
        fb_analyser: 'Recolección de resultados de Meta (FB e IG)',
        fb_history_analyser: 'Recolección histórica de resultados de Meta (FB e IG)',
        ig_analyser: 'Recolección de resultados de Instagram',
        ig_history_analyser: 'Recolección histórica de resultados de Instagram',
        billing_generator: 'Generación de facturación',
        billing_regenerator: 'Regeneración de facturación',
        generate_docs: 'Generación de documentos de facturación',
        billing_summary_generator: 'Generación de resumen de facturación'
      }

      return taskNames[text]
    }
  },
  methods: {
    async onProductBatchFileUploaded (file) {
      this.clearProductLoadingInterval()
      this.productUploadState = 'PENDING'
      try {
        await productsService.uploadBatchCSV(file)
        await this.getProductUploadState()
        this.setProductLoadingInterval()
        this.setProductMessage()
      } catch (err) {
        // eslint-disable-next-line
        console.log('[admin.js] Error uploading batch products CSV', err)
        this.productUploadState = null
        this.setProductUploadError()
      }
    },
    async onProductSimpleFileUploaded (file) {
      this.productSimpleUploadLoading = true
      try {
        await productsService.uploadSimpleCSV(file)
        this.productSimpleUploadLoading = false
        this.setProductMessage()
      } catch (err) {
        // eslint-disable-next-line
        console.log('[admin.js] Error uploading simple product CSV', err)
        this.productSimpleUploadLoading = false
        this.setProductUploadError()
      }
    },
    async onPriceFileUploaded (file) {
      this.priceUploadLoading = true
      try {
        await productsService.uploadPriceCSV(file)
        this.priceUploadLoading = false
        this.setProductMessage()
      } catch (err) {
        // eslint-disable-next-line
        console.log('[admin.js] Error uploading prices CSV', err)
        this.priceUploadLoading = false
        this.setProductUploadError()
      }
    },
    async downloadProductExample () {
      this.productDownloadLoading = true
      try {
        const data = await productsService.getProductExampleCSV()
        downloadBlob(data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'products_example.xlsx')
      } catch (err) {
        // eslint-disable-next-line
        console.error('[admin.js] Error downloading product CSV')
      }
      this.productDownloadLoading = false
    },
    async downloadProductPrices () {
      this.priceDownloadLoading = true
      try {
        const data = await productsService.getPriceCSV()
        downloadBlob(data, EXCEL_MIME_TYPE, 'product_prices.xlsx')
      } catch (err) {
        // eslint-disable-next-line
        console.error('[admin.js] Error downloading prices xlsx')
      }
      this.priceDownloadLoading = false
    },
    setProductMessage () {
      this.showProductMessage = true
      clearTimeout(this.productMessageTimer)
      this.productMessageTimer = setTimeout(() => {
        this.showProductMessage = false
      }, MESSAGE_TIMEOUT)
    },
    setProductUploadError () {
      this.productUploadError = true
      clearTimeout(this.productMessageTimer)
      this.productMessageTimer = setTimeout(() => {
        this.productUploadError = false
      }, MESSAGE_TIMEOUT)
    },
    setProductLoadingInterval () {
      this.clearProductLoadingInterval()
      this.productUploadInterval = setInterval(() => {
        this.getProductUploadState()
      }, PRODUCT_POLLING_INTERVAL)
    },
    clearProductLoadingInterval () {
      if (this.productUploadInterval) {
        clearInterval(this.productUploadInterval)
      }
    },
    async getProductUploadState () {
      try {
        this.productUploadState = await productsService.getBatchUploadState()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[admin.js] Error fetching product upload state', err)
      }
    },
    async getTemplates (options = { page_size: PAGE_SIZE }) {
      const templates = await templatesService.getTemplates(options)
      this.templates = templates
    },
    async getTargets (options = { page_size: PAGE_SIZE }) {
      const targets = await targetsService.getTargets(options)
      this.targets = targets
    },
    async getProducts (options = { page_size: PAGE_SIZE }) {
      const products = await productsService.getProducts(options, this.filters)
      this.products = products
    },
    async getConfs (options = { page_size: PAGE_SIZE }) {
      const confs = await confsService.getConfs(options)
      this.confs = confs
    },
    async getTasks (options = { page_size: PAGE_SIZE }) {
      const tasks = await tasksService.getTasks(options, this.filters)
      this.tasks = tasks
      this.tasksNumber = tasks.length
    },
    async fetchPage (page) {
      this.loading = true
      try {
        if (this.activeTab === 'templates') {
          await this.getTemplates({ page_size: PAGE_SIZE, page: page || 1 })
        }
        if (this.activeTab === 'targets') {
          await this.getTargets({ page_size: PAGE_SIZE, page: page || 1 })
        }
        if (this.activeTab === 'products') {
          await this.getProducts({ page_size: PAGE_SIZE, page: page || 1 })
        }
        if (this.activeTab === 'confs') {
          await this.getConfs({ page_size: PAGE_SIZE, page: page || 1 })
        }
        if (this.activeTab === 'tasks') {
          await this.getTasks({ page_size: PAGE_SIZE, page: page || 1 })
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error(`[admin.js] Error fetching page ${page} for ${this.activeTab}`, err)
      }
      this.loading = false
    },
    selectTab (tab) {
      if (this.activeTab === tab.id) { return }
      this.activeTab = tab.id
      this.fetchPage(1)
    },
    createTemplate () {
      this.$router.push('administration/template')
    },
    createTarget () {
      this.$router.push('administration/target')
    },
    viewDetail (id) {
      this.$router.push(`administration/template/${id}`)
    },
    onChangePage (pager) {
      this.fetchPage(pager.currentPage)
    },
    onUpdateElement ({ type, value }) {
      const index = this[type].results.findIndex(template => template.id === value.id)
      this[type].results[index] = value
    },
    onDeleteElement () {
      this.fetchPage()
    },
    onAddElement ({ type, value }) {
      if (this[type].results.length >= PAGE_SIZE) {
        this[type].results = [value, ...this[type].results.splice(0, this[type].results.length - 1)]
      } else {
        this[type].results.push(value)
      }
      this[type].meta.count += 1
    },
    onSelect (type, item) {
      const id = item.id
      if (type === 'templates') {
        this.$router.push(`/administration/template/${id}`)
      }
      if (type === 'targets') {
        this.$router.push(`/administration/target/${id}`)
      }
      if (type === 'confs') {
        this.$router.push(`/administration/conf/${id}`)
      }
    }
  }
}
